 import React, { useRef } from "react";
import { Link } from "react-router-dom";

import Navbar from "../../components/Navbar";
import SecondNavbar from "../../components/SecondNavbar";
import Footer from "../../components/Footer";

import "./land.css"; 

import landHeroBg from "../../assets/tree.jpg"; 
import erosionImg from "../../assets/crackedland.jpg";
import desertImg from "../../assets/greenland.webp";
import industryImg from "../../assets/industrialland.jpg";
import vehicleImg from "../../assets/tips.jpg";

const LandAwareness = () => {
  const threatsSection = useRef(null);
  const causesSection = useRef(null);
  const impactsSection = useRef(null);
  const solutionsSection = useRef(null);
  const metricsSection = useRef(null);
  const advancedSection = useRef(null);

  const handleScroll = (sectionRef) => {
    window.scrollTo({
      top: sectionRef.current.offsetTop - 120, 
      behavior: "smooth",
    });
  };

  return (
    <div className="premium-magazine-body land-module">
      <Navbar />
      <SecondNavbar />

      <section className="mag-hero-section">
        <div className="mag-hero-grid">
          <div className="mag-hero-text-side">
            <h1>Protect Our Land And <span className="highlight-text-vibe">Save Soil</span></h1>
            <p>
              Land and soil are the foundation of life on Earth. Use this premium guide 
              to learn why our global soil systems are degrading, how to analyze ground health, 
              and how we can collaboratively implement conservation strategies.
            </p>
          </div>
          <div className="mag-hero-image-side" style={{ backgroundImage: `url(${landHeroBg})` }}>
            <div className="mag-floating-badge">Save Our Nature</div>
          </div>
        </div>
      </section>

      <section className="mag-interactive-control-deck-sticky">
        <div className="control-deck-wrapper">
          <button className="control-deck-pill" onClick={() => handleScroll(threatsSection)}>
            <span className="pill-index">01</span> <span className="pill-label">Soil Damage</span>
          </button>
          <button className="control-deck-pill" onClick={() => handleScroll(causesSection)}>
            <span className="pill-index">02</span> <span className="pill-label">Main Causes</span>
          </button>
          <button className="control-deck-pill" onClick={() => handleScroll(impactsSection)}>
            <span className="pill-index">03</span> <span className="pill-label">Bad Impacts</span>
          </button>
          <button className="control-deck-pill" onClick={() => handleScroll(solutionsSection)}>
            <span className="pill-index">04</span> <span className="pill-label">Our Solutions</span>
          </button>
          <button className="control-deck-pill" onClick={() => handleScroll(metricsSection)}>
            <span className="pill-index">05</span> <span className="pill-label">Soil Health</span>
          </button>
          <button className="control-deck-pill" onClick={() => handleScroll(advancedSection)}>
            <span className="pill-index">06</span> <span className="pill-label">USDA Rules</span>
          </button>
        </div>
      </section>

      <section className="soil-metrics-dashboard" ref={metricsSection}>
        <div className="metrics-header">
          <span className="vector-number green-text-indicator">Nature Guide</span>
          <h2>What is Healthy Soil?</h2>
          <p>Optimal soil composition requires a strict balance of four core components. Disrupting this equilibrium accelerates land desertification:</p>
        </div>
        <div className="metrics-grid-cards">
          <div className="metric-card sync-card-green">
            <h3>45%</h3>
            <h4>Minerals</h4>
            <p>The fundamental structural components that supply vital nutrients and anchoring strength to vegetation.</p>
          </div>
          <div className="metric-card sync-card-orange">
            <h3>25%</h3>
            <h4>Water</h4>
            <p>Essential ground moisture that preserves chemical balances and prevents root dehydration during severe heatwaves.</p>
          </div>
          <div className="metric-card sync-card-green">
            <h3>25%</h3>
            <h4>Air</h4>
            <p>Microscopic subterranean pathways that allow root networks to respire efficiently and prevent root rot.</p>
          </div>
          <div className="metric-card sync-card-yellow">
            <h3>5%</h3>
            <h4>Organic Matter</h4>
            <p>Decomposed natural waste and humus that enrich the agricultural topsoil with biological fertility.</p>
          </div>
        </div>
      </section>

      <section className="land-long-feed-section">
        <div className="feed-container-wrapper">

          <div className="editorial-long-box" ref={threatsSection}>
            <div className="long-box-img-side">
              <img src={erosionImg} alt="Soil Damage" />
            </div>
            <div className="long-box-text-side">
              <h2>Why Soil is Getting Weak</h2>
              <p>
                Severe rainfall, flooding, and uninhibited winds systematically strip away the fertile topsoil layer. 
                Once this rich top layer is compromised, lush agricultural ecosystems gradually transition into arid, non-productive terrains.
              </p>
              <div className="section-bullet-points">
                <div className="bullet-item"><strong>Topsoil Erosion:</strong> Nutrients are actively washed away by water channels and strong drafts.</div>
                <div className="bullet-item"><strong>Desertification:</strong> Rapid and permanent loss of vegetation capacity transforms fertile land into a desert biome.</div>
              </div>
            </div>
          </div>

          <div className="editorial-long-box reverse-row" ref={causesSection}>
            <div className="long-box-img-side">
              <img src={desertImg} alt="Human Actions" />
            </div>
            <div className="long-box-text-side">
              <h2>What Causes This Damage?</h2>
              <p>
                Anthropogenic activities directly degrade the delicate subterranean soil structural integrity. Mechanical over-plowing breaks down natural aggregates, making dirt vulnerable to erosion, while excessive pesticide use eliminates beneficial micro-organisms.
              </p>
              <div className="section-bullet-points">
                <div className="bullet-item"><strong>Intensive Cultivation:</strong> Industrial over-tilling destroys the natural internal composition of farming land.</div>
                <div className="bullet-item"><strong>Chemical Additives:</strong> Excessive utilization of toxic chemical fertilizers diminishes long-term biological rejuvenation.</div>
              </div>
            </div>
          </div>

          <div className="editorial-long-box" ref={impactsSection}>
            <div className="long-box-img-side">
              <img src={industryImg} alt="Bad Results" />
            </div>
            <div className="long-box-text-side">
              <h2>How It Affects Our World</h2>
              <p>
                As arable land quality depreciates globally, structural food crop production indices plummet dramatically. Furthermore, industrial chemical runoff infiltrates local watersheds, severely contaminating deep ground reserves used for drinking.
              </p>
              <div className="section-bullet-points">
                <div className="bullet-item"><strong>Agricultural Scarcity:</strong> Chronic crop failures trigger severe food supply shortages across international markets.</div>
                <div className="bullet-item"><strong>Aquifer Toxicity:</strong> Dangerous industrial chemicals seep through subterranean layers, spoiling critical fresh water reserves.</div>
              </div>
            </div>
          </div>

          <div className="editorial-long-box reverse-row" ref={solutionsSection}>
            <div className="long-box-img-side">
              <img src={vehicleImg} alt="Solutions" />
            </div>
            <div className="long-box-text-side">
              <h2>Simple Ways to Save Our Land</h2>
              <p>
                We can systematically counteract land degradation by establishing indigenous agroforestry buffers around crop sectors. Implementing low-disturbance agricultural techniques ensures deep soil profiles remain biologically sound.
              </p>
              <div className="section-bullet-points">
                <div className="bullet-item"><strong>Afforestation Barriers:</strong> Cultivating local tree networks strategically intercepts heavy wind currents and flood waters.</div>
                <div className="bullet-item"><strong>Conservation Agriculture:</strong> Growing food supplies without disrupting or overturning structural organic horizons.</div>
              </div>
            </div>
          </div>

        </div>
      </section>

      <section className="usda-advanced-framework-section" ref={advancedSection}>
        <div className="usda-framework-header">
          <span className="vector-number orange-text-indicator">Best Practices</span>
          <h2>Official Ways to Protect Land</h2>
          <p>
            According to strategic frameworks established by international agricultural departments, 
            these three foundational methods effectively safeguard terrestrial ecosystems from degradation:
          </p>
        </div>

        <div className="usda-methods-magazine-layout">
          <div className="method-editorial-card border-accent-vibe">
            <div className="method-badge color-badge-green">Plan 1</div>
            <h3>Mitigating Arid Land Desertification</h3>
            <p>
              To stop sub-surfaces from drying up entirely, managed rotational grazing setups must be enforced. 
              Our frameworks guide local agricultural committees regarding modern micro-irrigation tools.
            </p>
            <span className="card-action-text-placeholder">🌱 Field Analysis Active</span>
          </div>

          <div className="method-editorial-card border-accent-orange">
            <div className="method-badge color-badge-orange">Plan 2</div>
            <h3>Stabilizing Fragile Soil Profiles</h3>
            <p>
              Land surfaces highly susceptible to weather erosion require structural vegetative support. 
              Deploying protective grass boundaries along degraded farming belts secures topsoil matrices.
            </p>
            <span className="card-action-text-placeholder">🌍 Regional Support Strategy</span>
          </div>

          <div className="method-editorial-card border-accent-yellow">
            <div className="method-badge color-badge-yellow">Plan 3</div>
            <h3>Implementing Cover Crop Rotations</h3>
            <p>
              Agricultural soil fields should never remain completely bare post-harvest. Deploying specific cover crops 
              acts as a living blanket, securing structural integrity against intense solar radiation and rainfall impact.
            </p>
            <span className="card-action-text-placeholder">🌾 Long-term Soil Goal</span>
          </div>
        </div>
      </section>

      <section className="be-part-of-change-container">
        <h2>Transform Knowledge Into Ecological Action 🌍</h2>
        <p>
          Reading about environmental guidelines is only the first step to securing natural resources. 
          Establishing automated grid parameters and community tracking setups allows environmental platforms 
          to log real-time geological improvements and coordinate clean-up systems efficiently.
        </p>
        <div className="action-banner-buttons-wrapper">
          <Link to="/join-as-volunteer" className="btn-join-volunteer">
            Join as Volunteer
          </Link>
          <Link to="/Event" className="btn-join-event">
            Explore Events
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};
  
export default LandAwareness;